import React from "react"
import PropTypes from "prop-types"
import { Flex, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { CALENDAR_TABS } from "../../constants"
import styles from "../../Calendar.module.css"

/**
 * All Bookings / My Bookings / Crew / Equipment.
 *
 * Filled tabs rather than buttons: the selected one is a blue surface with blue text, the rest sit
 * on Surface 2 with no border. They are plain `<button>`s for the same reason the Day/Week/Month
 * group in the row below is — an antd Button's own type styling is the opposite of this (solid
 * primary when selected, outlined when not) and would have to be undone rule by rule.
 */
const BookingTypeTabs = ({ activeTab, hasMyBookings, onChangeFilters, actions }) => {
  const { t } = useTranslation()

  const handleTabChange = (tab) => onChangeFilters({ target: { name: "tab", value: tab } })

  const tabs = [
    { key: CALENDAR_TABS.ALL_BOOKINGS, label: t("calendar.tabs.all_bookings", "All Bookings") },
    { key: CALENDAR_TABS.MY_BOOKINGS, label: t("calendar.tabs.my_bookings", "My Bookings"), isDisabled: !hasMyBookings },
    { key: CALENDAR_TABS.CREW, label: t("calendar.tabs.crew", "Crew") },
    { key: CALENDAR_TABS.EQUIPMENT, label: t("calendar.tabs.equipment", "Equipment") }
  ]

  const renderTab = ({ key, label, isDisabled }) => (
    <button
      key={key}
      type="button"
      className={`${styles.typeTab} ${activeTab === key ? styles.typeTabActive : ""}`}
      disabled={isDisabled}
      onClick={() => handleTabChange(key)}
    >
      {label}
    </button>
  )

  return (
    <Flex gap={8} align="center" className={`px-s py-xs ${styles.bookingTypeButtons}`}>
      {tabs.map((tab) =>
        tab.key === CALENDAR_TABS.MY_BOOKINGS && tab.isDisabled ? (
          <Tooltip key={tab.key} title={t("calendar.tabs.no_my_bookings", "No bookings assigned to you")}>
            <span>{renderTab(tab)}</span>
          </Tooltip>
        ) : (
          renderTab(tab)
        )
      )}
      {actions && <div className={styles.bookingTypeActions}>{actions}</div>}
    </Flex>
  )
}

BookingTypeTabs.propTypes = {
  activeTab: PropTypes.string.isRequired,
  hasMyBookings: PropTypes.bool,
  onChangeFilters: PropTypes.func.isRequired,
  /** Page actions pinned to the right of the tabs, where the design puts them. */
  actions: PropTypes.node,
}

export default BookingTypeTabs
